import React from "react";
import AppBar from "@material-ui/core/AppBar";
import Toolbar from "@material-ui/core/Toolbar";
import IconButton from "@material-ui/core/IconButton";
import KeyboardBackspaceIcon from "@material-ui/icons/KeyboardBackspace";
import SearchIcon from "@material-ui/icons/Search";
import CloseIcon from "@material-ui/icons/Close";
import Collapse from "@material-ui/core/Collapse";
import InputBase from "@material-ui/core/InputBase";
import Typography from "@material-ui/core/Typography";
import useStyles from "./styles";
import clsx from "clsx";
import MdSearch from "react-ionicons/lib/MdSearch";
import { useTranslation } from "react-i18next";

export interface HeaderBarSub2Props {
  name?: string;
  isSearch?: boolean;
  openUrl: (url: string) => void;
  backUrl?: string;
  handleSearch?: (keyword: string) => void;
  handleCloseSearch?: () => void;
  login?: boolean | false;
  searchPlaceholder?: string;
}

const HeaderBarSub2 = React.memo((props: HeaderBarSub2Props) => {
  const classes = useStyles();
  const {
    name,
    isSearch,
    openUrl,
    backUrl,
    handleSearch,
    login,
    searchPlaceholder,
  } = props;
  const translate = useTranslation().t;
  const [isOpenSearch, setIsOpenSearch] = React.useState(false);
  const [searchValue, setSearchValue] = React.useState("");
  const handleSearchRef = React.useRef(handleSearch);
  const lastSearchedRef = React.useRef<string | null>(null);

  const redirectTo = () => {
    backUrl ? openUrl(backUrl) : openUrl("/");
  };

  const handleChangeSearch = (value: string) => {
    setSearchValue(value);
  };

  React.useEffect(() => {
    handleSearchRef.current = handleSearch;
  }, [handleSearch]);

  // Debounce and avoid duplicate searches across rerenders.
  React.useEffect(() => {
    if (!handleSearchRef.current) return;
    if (!isOpenSearch && !searchValue) return;
    const trimmed = searchValue.trim();
    if (trimmed === "" && lastSearchedRef.current === null) return;
    const timer = window.setTimeout(() => {
      if (lastSearchedRef.current === trimmed) return;
      lastSearchedRef.current = trimmed;
      handleSearchRef.current?.(trimmed);
    }, 500);
    return () => window.clearTimeout(timer);
  }, [searchValue, isOpenSearch]);

  const handleCancelSearch = () => {
    setSearchValue("");
    setIsOpenSearch(false);
    handleSearchRef.current?.("");
  };

  const renderSearchBar = () => {
    if (!isSearch) return null;
    return (
      <Collapse in={isOpenSearch}>
        <div className={classes.searchWrapper}>
          <div className={classes.searchRow}>
            <div className={classes.searchContainer}>
              <div className={classes.searchIcon}>
                <SearchIcon />
              </div>
              <InputBase
                value={searchValue}
                onChange={(e) => handleChangeSearch(e.target.value)}
                placeholder={searchPlaceholder || "Search"}
                className={classes.searchInput}
                inputProps={{ "aria-label": "search" }}
              />
              {!!searchValue && (
                <IconButton size="small" onClick={() => handleChangeSearch("")}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              )}
            </div>
            <span onClick={handleCancelSearch} className={classes.cancelText}>
              {translate("CANCEL")}
            </span>
          </div>
        </div>
      </Collapse>
    );
  };

  return (
    <AppBar className={classes.toolbar} position="sticky">
      <Toolbar className={classes.containerHeader}>
        {/* Back icon */}
        <IconButton
          onClick={redirectTo}
          edge="start"
          className={clsx({ [classes.fontSmall]: login }, classes.menuButton)}
          color="inherit"
          aria-label="menu"
        >
          {login ? translate("SIGN_IN") : <KeyboardBackspaceIcon />}
        </IconButton>

        {/* Title */}
        <Typography
          variant="h6"
          className={clsx({ [classes.fontHarabara]: !name }, classes.title)}
        >
          {name ? <p>{name}</p> : "ShelterApp"}
        </Typography>

        {/* Search icon */}
        {isSearch && (
          <IconButton
            onClick={() => setIsOpenSearch(true)}
            className={classes.iconphone}
            edge="end"
            color="inherit"
          >
            <MdSearch color="white" fontSize="24px" />
          </IconButton>
        )}
      </Toolbar>
      {renderSearchBar()}
    </AppBar>
  );
});

export default HeaderBarSub2;
